<template>
  <div>
    <!-- Botão Flutuante de IA -->
    <div
      v-if="!chatAIAberto"
      class="ai-chat-button"
      :title="'Conversar com IA'"
      @click="abrirChatAI"
    >
      <div class="ai-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <circle
            cx="9"
            cy="10"
            r="1"
          />
          <circle
            cx="15"
            cy="10"
            r="1"
          />
          <path d="M9 14s1 1 3 1 3-1 3-1" />
        </svg>
      </div>
      <div class="ai-badge">
        IA
      </div>
    </div>

    <!-- Chat de IA -->
    <transition name="slide-up">
      <div
        v-if="chatAIAberto"
        class="ai-chat-container"
      >
        <!-- Cabeçalho do Chat -->
        <div class="ai-chat-header">
          <div class="d-flex align-items-center">
            <div class="ai-avatar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div class="ml-2">
              <h6 class="mb-0">
                Assistente IA
              </h6>
              <small class="text-white-50">Online</small>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              class="btn-icon"
              title="Minimizar"
              @click="minimizarChatAI"
            >
              <feather-icon
                icon="MinusIcon"
                size="18"
              />
            </button>
            <button
              class="btn-icon"
              title="Fechar"
              @click="fecharChatAI"
            >
              <feather-icon
                icon="XIcon"
                size="18"
              />
            </button>
          </div>
        </div>

        <!-- Corpo do Chat -->
        <div
          ref="chatBody"
          class="ai-chat-body"
        >
          <!-- Mensagem de boas-vindas -->
          <div class="message message-ai">
            <div class="message-avatar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div class="message-content">
              <p class="mb-1">
                👋 Olá! Sou seu assistente de IA da Many.
              </p>
              <p class="mb-2">
                Como posso ajudar hoje?
              </p>
              <p class="mb-0">
                <small class="text-muted">💡 Pergunte sobre suas vendas, clientes, segmentos RFM, produtos mais vendidos e muito mais!</small>
              </p>
            </div>
          </div>

          <!-- Mensagens do chat -->
          <div
            v-for="(mensagem, index) in mensagensChat"
            :key="index"
            :class="['message', mensagem.tipo === 'user' ? 'message-user' : 'message-ai']"
          >
            <div
              v-if="mensagem.tipo === 'ai'"
              class="message-avatar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div class="message-content">
              <div v-if="mensagem.tipo === 'ai'">
                <div
                  class="ai-response"
                  v-html="formatarResposta(mensagem.texto)"
                />

                <!-- Resumo de dados se houver -->
                <div
                  v-if="mensagem.data && mensagem.data.store_summary"
                  class="store-summary-card mt-3"
                >
                  <small class="text-muted d-block mb-2">📊 Dados utilizados na resposta:</small>
                  <div class="summary-grid">
                    <div class="summary-item">
                      <small class="text-muted">Faturamento</small>
                      <strong>R$ {{ formatarMoeda(mensagem.data.store_summary.faturamento || 0) }}</strong>
                    </div>
                    <div class="summary-item">
                      <small class="text-muted">Pedidos</small>
                      <strong>{{ mensagem.data.store_summary.total_pedidos || 0 }}</strong>
                    </div>
                    <div class="summary-item">
                      <small class="text-muted">Clientes</small>
                      <strong>{{ mensagem.data.store_summary.clientes_unicos || 0 }}</strong>
                    </div>
                    <div class="summary-item">
                      <small class="text-muted">Ticket Médio</small>
                      <strong>R$ {{ formatarMoeda(mensagem.data.store_summary.ticket_medio || 0) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <p
                v-else
                class="mb-0"
              >
                {{ mensagem.texto }}
              </p>
              <small class="message-time">{{ mensagem.hora }}</small>
            </div>
          </div>

          <!-- Indicador de digitando -->
          <div
            v-if="aiDigitando"
            class="message message-ai"
          >
            <div class="message-avatar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>

        <!-- Rodapé do Chat (Input) -->
        <div class="ai-chat-footer">
          <div class="chat-input-container">
            <input
              v-model="mensagemAtual"
              type="text"
              class="chat-input"
              placeholder="Digite sua mensagem..."
              @keyup.enter="enviarMensagem"
            >
            <button
              class="btn-send"
              :disabled="!mensagemAtual.trim()"
              @click="enviarMensagem"
            >
              <feather-icon
                icon="SendIcon"
                size="18"
              />
            </button>
          </div>
          <small class="text-muted d-block mt-2 text-center">
            🤖 Powered by ManyConvert AI
          </small>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { chatWithIA } from '@/functions/basics/analytics.js'
import { formatarMoeda } from '../services/dashboardService'

export default {
  name: 'AIChatButton',
  components: {
    FeatherIcon,
  },
  props: {
    storeUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chatAIAberto: false,
      mensagemAtual: '',
      mensagensChat: [],
      aiDigitando: false,
      conversationId: null,
    }
  },
  methods: {
    formatarMoeda(valor) {
      return formatarMoeda(valor)
    },

    formatarResposta(texto) {
      if (!texto) return ''

      let html = texto

      // Processar markdown em ordem específica
      html = html.replace(/^### \*\*(.*?)\*\*:/gm, '<h6 class="mb-2 mt-3"><strong>$1</strong></h6>')
      html = html.replace(/^### (.*?):/gm, '<h6 class="mb-2 mt-3">$1:</h6>')
      html = html.replace(/^## (.*$)/gm, '<h5 class="mb-2 mt-3">$1</h5>')
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/^(\d+)\. (.*$)/gm, '<li-num data-num="$1">$2</li-num>')
      html = html.replace(/^- (.*$)/gm, '<li-bullet>$1</li-bullet>')
      html = html.replace(/\n\n/g, '</p><p class="mb-2">')
      html = html.replace(/\n/g, '<br>')

      // Envolver listas numeradas em <ol>
      if (html.includes('<li-num')) {
        html = html.replace(/(<li-num.*?<\/li-num>(<br>)?)+/g, match => {
          const items = match.match(/<li-num data-num="(\d+)">(.*?)<\/li-num>/g) || []
          return `<ol class="mb-2">${items.map(item => {
            const [, num, content] = item.match(/<li-num data-num="(\d+)">(.*?)<\/li-num>/) || []
            return `<li>${content}</li>`
          }).join('')}</ol>`
        })
      }

      // Envolver listas com marcadores em <ul>
      if (html.includes('<li-bullet')) {
        html = html.replace(/(<li-bullet>.*?<\/li-bullet>(<br>)?)+/g, match => {
          const items = match.match(/<li-bullet>(.*?)<\/li-bullet>/g) || []
          return `<ul class="mb-2">${items.map(item => {
            const [, content] = item.match(/<li-bullet>(.*?)<\/li-bullet>/) || []
            return `<li>${content}</li>`
          }).join('')}</ul>`
        })
      }

      // Envolver em parágrafo se não começar com tag
      if (!html.startsWith('<')) {
        html = `<p class="mb-2">${html}</p>`
      }

      return html
    },

    sanitizarRespostaIA(resposta) {
      if (!resposta) return 'Desculpe, não consegui processar sua pergunta.'

      // Remove menções técnicas sobre ADX, MySQL, bases de dados
      const respostaSanitizada = resposta
        .replace(/ADX/gi, 'base de dados')
        .replace(/MySQL/gi, 'base de dados')
        .replace(/não encontrei nenhum dado no ADX ou MySQL/gi, 'não encontrei dados para o período solicitado')
        .replace(/não encontrei nenhum dado na base de dados/gi, 'não encontrei dados para o período solicitado')
        .replace(/não há dados no ADX ou MySQL/gi, 'não há dados disponíveis')
        .replace(/erro ao consultar ADX ou MySQL/gi, 'erro ao consultar os dados')
        .replace(/dados não encontrados no ADX ou MySQL/gi, 'dados não encontrados')

      // Se a resposta contém informações técnicas sobre não encontrar dados
      if (respostaSanitizada.toLowerCase().includes('não encontrei')
          && respostaSanitizada.toLowerCase().includes('base de dados')) {
        return '📊 Não encontrei dados para o período solicitado. Tente ajustar o período da sua pergunta ou verifique se há dados disponíveis para análise.'
      }

      return respostaSanitizada
    },

    abrirChatAI() {
      this.chatAIAberto = true
    },

    fecharChatAI() {
      this.chatAIAberto = false
    },

    minimizarChatAI() {
      this.chatAIAberto = false
    },

    async enviarMensagem() {
      if (!this.mensagemAtual.trim()) return

      if (!this.storeUuid) {
        this.$bvToast.toast('Selecione uma loja antes de conversar com a IA', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      const agora = new Date()
      const hora = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

      // Adicionar mensagem do usuário
      this.mensagensChat.push({
        tipo: 'user',
        texto: this.mensagemAtual,
        hora,
      })

      // Limpar input
      const mensagemEnviada = this.mensagemAtual
      this.mensagemAtual = ''

      // Scroll para o final
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight
        }
      })

      // Chamar API da IA
      this.aiDigitando = true

      try {
        const response = await chatWithIA(this.storeUuid, mensagemEnviada, {
          conversation_id: this.conversationId || this.storeUuid,
        })

        this.aiDigitando = false

        if (response && response.data) {
          // Salvar conversation_id se houver
          if (response.data.conversation_id) {
            this.conversationId = response.data.conversation_id
          }

          // Adicionar resposta da IA (sanitizada)
          const respostaSanitizada = this.sanitizarRespostaIA(response.data.answer)

          this.mensagensChat.push({
            tipo: 'ai',
            texto: respostaSanitizada,
            hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            data: response.data,
          })
        } else {
          throw new Error('Resposta inválida da API')
        }
      } catch (error) {
        console.error('❌ Erro ao chamar IA:', error)
        this.aiDigitando = false

        // Adicionar mensagem de erro
        this.mensagensChat.push({
          tipo: 'ai',
          texto: '⚠️ Desculpe, ocorreu um erro ao processar sua pergunta. Por favor, tente novamente.',
          hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        })

        this.$bvToast.toast('Erro ao comunicar com a IA', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      }

      // Scroll para o final
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight
        }
      })
    },
  },
}
</script>

<style scoped src="../styles/ai-chat-styles.css"></style>
